import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import {
  Sparkles,
  FileText,
  CheckCircle,
  Clock,
  ShieldCheck,
  BadgeCheck,
  Zap,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

// ---------- Phase 1: AI Quote Generation ----------
const Phase1 = () => {
  const { t } = useLanguage();
  const tags = [
    t("salesAnim.tagBiz"),
    t("salesAnim.tagParams"),
    t("salesAnim.tagSLA"),
    t("salesAnim.tagStd"),
  ];
  const rows = [
    { label: t("salesAnim.rowBase"), value: 4820 },
    { label: t("salesAnim.rowExtra"), value: 620 },
    { label: t("salesAnim.rowCycle"), value: 14, suffix: "d" },
    { label: t("salesAnim.rowTerms"), value: 100, suffix: "%" },
  ];

  const [rowsShown, setRowsShown] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setRowsShown(0);
    setDone(false);
    const timers: ReturnType<typeof setTimeout>[] = [];
    rows.forEach((_, i) => {
      timers.push(setTimeout(() => setRowsShown(i + 1), 500 + i * 250));
    });
    timers.push(setTimeout(() => setDone(true), 500 + rows.length * 250 + 200));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative h-full grid grid-cols-2 gap-3">
      {/* Left: needs input */}
      <div className="relative rounded-xl p-3 backdrop-blur-md bg-white/5 border border-white/10 shadow-inner">
        <div className="text-[10px] text-cyan-300 mb-2 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" />
          {t("salesAnim.needsInput")}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {tags.map((tag, i) => (
            <motion.span
              key={tag}
              initial={{ opacity: 0, y: 8, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.08 + i * 0.08, type: "spring", stiffness: 300 }}
              className="text-[9px] px-2 py-1 rounded-md bg-cyan-500/15 border border-cyan-400/30 text-cyan-200"
            >
              {tag}
            </motion.span>
          ))}
        </div>
      </div>

      {/* Data flow */}
      <div className="pointer-events-none absolute left-[46%] right-[46%] top-1/2 -translate-y-1/2 h-[2px]">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-emerald-400 to-transparent"
          initial={{ scaleX: 0, transformOrigin: "left" }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        />
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-cyan-300"
            style={{ boxShadow: "0 0 8px rgba(45,212,191,0.9)" }}
            initial={{ x: 0, opacity: 0 }}
            animate={{ x: 40, opacity: [0, 1, 0] }}
            transition={{ duration: 0.6, delay: 0.5 + i * 0.15, repeat: Infinity, repeatDelay: 0.2 }}
          />
        ))}
      </div>

      {/* Right: quote sheet */}
      <div className="relative rounded-xl p-3 bg-slate-800/60 border border-slate-600/40 overflow-hidden">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] text-slate-200 font-medium flex items-center gap-1">
            <FileText className="w-3 h-3 text-emerald-300" />
            {t("salesAnim.quoteTitle")}
          </div>
          <AnimatePresence>
            {done && (
              <motion.span
                initial={{ opacity: 0, scale: 0.6, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 flex items-center gap-1"
              >
                <CheckCircle className="w-2.5 h-2.5" />
                {t("salesAnim.aiDone")}
              </motion.span>
            )}
          </AnimatePresence>
        </div>
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <motion.div
              key={r.label}
              initial={{ opacity: 0, y: 6 }}
              animate={{
                opacity: rowsShown > i ? 1 : 0.15,
                y: rowsShown > i ? 0 : 6,
              }}
              transition={{ duration: 0.35 }}
              className="flex items-center justify-between text-[9px] px-1.5 py-1 rounded bg-slate-900/60 border border-slate-700/40"
            >
              <span className="text-slate-300">{r.label}</span>
              <RollingNumber active={rowsShown > i} target={r.value} suffix={r.suffix ?? ""} />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

const RollingNumber = ({
  active,
  target,
  suffix,
}: {
  active: boolean;
  target: number;
  suffix?: string;
}) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) {
      setN(0);
      return;
    }
    let raf: number;
    const start = performance.now();
    const dur = 400;
    const step = (now: number) => {
      const p = Math.min(1, (now - start) / dur);
      setN(Math.floor(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, target]);
  return (
    <span className="tabular-nums text-emerald-300 font-medium">
      {suffix === "%" || suffix === "d" ? n : n.toLocaleString()}
      {suffix ?? ""}
    </span>
  );
};

// ---------- Phase 2: Efficiency Upgrade ----------
const Phase2 = () => {
  const { t } = useLanguage();
  const [manual, setManual] = useState(0);
  const [ai, setAi] = useState(0);
  const [showUplift, setShowUplift] = useState(false);

  useEffect(() => {
    setManual(0);
    setAi(0);
    setShowUplift(false);
    const m = setInterval(() => setManual((v) => (v >= 100 ? 100 : v + 3.5)), 25);
    const a = setInterval(() => setAi((v) => (v >= 100 ? 100 : v + 12)), 25);
    const s = setTimeout(() => setShowUplift(true), 1100);
    return () => {
      clearInterval(m);
      clearInterval(a);
      clearTimeout(s);
    };
  }, []);

  return (
    <div className="h-full flex flex-col justify-center gap-4 px-2">
      {/* Manual */}
      <div>
        <div className="flex items-center justify-between text-[10px] mb-1">
          <div className="flex items-center gap-1.5 text-slate-300">
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
              className="inline-flex"
            >
              <Clock className="w-3 h-3 text-slate-400" />
            </motion.span>
            {t("salesAnim.manual")}
          </div>
          <span className="text-slate-400">{t("salesAnim.hours")}</span>
        </div>
        <div className="h-2 rounded-full bg-slate-700/60 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-slate-500 to-slate-400 transition-[width] duration-100"
            style={{ width: `${manual}%` }}
          />
        </div>
      </div>

      {/* AI */}
      <div>
        <div className="flex items-center justify-between text-[10px] mb-1">
          <div className="flex items-center gap-1.5 text-emerald-300">
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
              className="inline-flex"
            >
              <Zap className="w-3 h-3 text-emerald-300" />
            </motion.span>
            {t("salesAnim.ai")}
          </div>
          <span className="text-emerald-300">{t("salesAnim.minutes")}</span>
        </div>
        <div className="h-2 rounded-full bg-slate-700/60 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-400 via-cyan-400 to-teal-300 transition-[width] duration-75"
            style={{ width: `${ai}%` }}
          />
        </div>
      </div>

      <AnimatePresence>
        {showUplift && (
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: [0.6, 1.15, 1] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto mt-1 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-400/40"
          >
            <div className="text-center text-lg font-bold bg-gradient-to-r from-emerald-300 to-cyan-300 bg-clip-text text-transparent">
              {t("salesAnim.uplift")}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------- Phase 3: Professional Quotation ----------
const Phase3 = () => {
  const { t } = useLanguage();
  const checks = [
    t("salesAnim.checkPrice"),
    t("salesAnim.checkMarket"),
    t("salesAnim.checkTerms"),
    t("salesAnim.checkBrand"),
    t("salesAnim.checkRisk"),
  ];
  const rows = [
    { label: t("salesAnim.rowBase"), value: "¥4,820.00" },
    { label: t("salesAnim.rowExtra"), value: "¥620.00" },
    { label: t("salesAnim.rowCycle"), value: "14 days" },
    { label: t("salesAnim.rowTerms"), value: "Standard" },
  ];
  const [shown, setShown] = useState(0);
  const [formal, setFormal] = useState(false);

  useEffect(() => {
    setShown(0);
    setFormal(false);
    const timers: ReturnType<typeof setTimeout>[] = [];
    checks.forEach((_, i) => {
      timers.push(setTimeout(() => setShown(i + 1), 300 + i * 500));
    });
    timers.push(setTimeout(() => setFormal(true), 300 + checks.length * 500 + 300));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative h-full flex flex-col gap-2">
      {/* Check badges row - top */}
      <div className="flex items-center justify-between gap-1">
        {checks.map((label, i) => {
          const active = shown > i;
          return (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: -6 }}
              animate={{
                opacity: 1,
                y: 0,
                scale: active ? 1 : 0.96,
              }}
              transition={{ duration: 0.3 }}
              className={`flex-1 flex items-center justify-center gap-1 px-1 py-1 rounded-md border transition-colors duration-300 ${
                active
                  ? "bg-emerald-500/20 border-emerald-400/50"
                  : "bg-slate-800/40 border-slate-700/40"
              }`}
            >
              <motion.span
                animate={{ scale: active ? [0.6, 1.25, 1] : 0.6 }}
                transition={{ duration: 0.4 }}
                className="inline-flex"
              >
                <CheckCircle
                  className={`w-3 h-3 ${active ? "text-emerald-300" : "text-slate-500"}`}
                />
              </motion.span>
              <span
                className={`text-[8px] whitespace-nowrap ${
                  active ? "text-emerald-200" : "text-slate-500"
                }`}
              >
                {label}
              </span>
            </motion.div>
          );
        })}
      </div>

      {/* Quote sheet - realistic document */}
      <motion.div
        animate={{
          borderColor: formal ? "rgba(52,211,153,0.7)" : "rgba(148,163,184,0.3)",
          boxShadow: formal
            ? "0 8px 30px -8px rgba(16,185,129,0.35)"
            : "0 4px 16px -6px rgba(0,0,0,0.4)",
        }}
        transition={{ duration: 0.7 }}
        className="relative flex-1 rounded-md border overflow-hidden bg-gradient-to-b from-slate-800 to-slate-900"
      >
        {/* Brand header */}
        <div
          className={`px-3 py-1.5 flex items-center justify-between border-b transition-colors duration-500 ${
            formal
              ? "bg-gradient-to-r from-emerald-600 to-cyan-600 border-emerald-500/50"
              : "bg-slate-800/80 border-slate-700/60"
          }`}
        >
          <div className="flex items-center gap-1.5">
            <div
              className={`w-4 h-4 rounded-sm flex items-center justify-center ${
                formal ? "bg-white/20" : "bg-slate-600/50"
              }`}
            >
              <Sparkles className={`w-2.5 h-2.5 ${formal ? "text-white" : "text-slate-300"}`} />
            </div>
            <span
              className={`text-[9px] font-bold tracking-wider ${
                formal ? "text-white" : "text-slate-200"
              }`}
            >
              NOVASALES
            </span>
          </div>
          <span
            className={`text-[7px] font-medium ${
              formal ? "text-white/80" : "text-slate-400"
            }`}
          >
            {t("salesAnim.formal")} · Q-2026-0706
          </span>
        </div>

        {/* Sheet body */}
        <div className="px-3 py-2 space-y-1.5">
          {/* Table header */}
          <div className="flex items-center justify-between text-[7px] font-semibold text-slate-400 uppercase tracking-wider pb-0.5 border-b border-slate-700/60">
            <span>Item</span>
            <span>Amount</span>
          </div>
          {rows.map((r, i) => (
            <motion.div
              key={r.label}
              initial={{ opacity: 0.5 }}
              animate={{ opacity: 1 }}
              transition={{ delay: i * 0.08 }}
              className="flex items-center justify-between text-[9px] py-0.5 border-b border-slate-700/40"
            >
              <span className="text-slate-300">{r.label}</span>
              <span className="tabular-nums font-medium text-slate-200">{r.value}</span>
            </motion.div>
          ))}

          {/* Total */}
          <motion.div
            animate={{
              backgroundColor: formal ? "rgba(16,185,129,0.12)" : "rgba(148,163,184,0.1)",
              borderColor: formal ? "rgba(52,211,153,0.5)" : "rgba(148,163,184,0.25)",
            }}
            className="flex items-center justify-between text-[10px] font-bold px-1.5 py-1 mt-1 rounded border"
          >
            <span className="text-slate-200">TOTAL</span>
            <span
              className={`tabular-nums ${formal ? "text-emerald-300" : "text-slate-300"}`}
            >
              ¥5,440.00
            </span>
          </motion.div>
        </div>

        {/* Formal completion overlay - stamp/seal */}
        <AnimatePresence>
          {formal && (
            <motion.div
              initial={{ opacity: 0, scale: 0.4, rotate: -20 }}
              animate={{ opacity: 1, scale: 1, rotate: -12 }}
              exit={{ opacity: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="absolute right-3 bottom-2 flex items-center gap-1 px-2 py-1 rounded-md border-2 border-emerald-400/70 bg-emerald-500/15 backdrop-blur-xs"
            >
              <BadgeCheck className="w-3.5 h-3.5 text-emerald-300" />
              <span className="text-[8px] font-bold text-emerald-300 tracking-wide">
                {t("salesAnim.aiDone")}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};



// ---------- Root Card ----------
export const SalesEmployeeCard = () => {
  const { t } = useLanguage();
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    // 3 phases, ~6s each
    const durations = [3200, 3500, 5500];
    let i = 0;
    setPhase(0);
    const tick = () => {
      i = (i + 1) % 3;
      setPhase(i);
    };
    let timer = setTimeout(function loop() {
      tick();
      timer = setTimeout(loop, durations[i]);
    }, durations[0]);
    return () => clearTimeout(timer);
  }, []);

  const labels = [
    t("salesAnim.phase1"),
    t("salesAnim.phase2"),
    t("salesAnim.phase3"),
  ];

  return (
    <div className="relative w-full h-[420px] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl overflow-hidden border border-slate-700/50">
      {/* Background */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <div className="relative p-4 border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center border border-emerald-500/30">
            <Sparkles className="w-5 h-5 text-emerald-300" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">{t("salesWorker.title")}</h3>
            <p className="text-[10px] text-slate-400">{t("salesAnim.subtitle")}</p>
          </div>
          <div className="ml-auto flex items-center gap-1.5 px-2 py-1 bg-slate-800/60 rounded-full border border-slate-600/40">
            <ShieldCheck className="w-3 h-3 text-emerald-300" />
            <span className="text-[10px] text-slate-300 font-medium">{labels[phase]}</span>
          </div>
        </div>
      </div>

      {/* Phase indicator */}
      <div className="relative px-4 pt-3 flex items-center gap-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full ${
              phase === i
                ? "bg-gradient-to-r from-emerald-400 to-cyan-400"
                : phase > i
                ? "bg-emerald-500/40"
                : "bg-slate-700/60"
            }`}
          />
        ))}
      </div>

      {/* Body */}
      <div className="relative p-4 h-[300px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={phase}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="h-full"
          >
            {phase === 0 && <Phase1 />}
            {phase === 1 && <Phase2 />}
            {phase === 2 && <Phase3 />}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-700/50 bg-slate-900/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] text-slate-400">{t("salesAnim.running")}</span>
          </div>
          <span className="text-[9px] text-slate-500">
            {phase + 1}/3 · {labels[phase]}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SalesEmployeeCard;
